import React, { useContext, useState } from 'react';
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { Button } from '../../../../catalyst-components/button';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { BaseContext } from '../../../../shared/base-context';
import { Alert, AlertActions, AlertDescription, AlertTitle } from '../../../../catalyst-components/alert';
import { ConfirmationDialog } from '../../../../catalyst-components/confirmation-dialog';
import { Dialog, DialogBody, DialogTitle, DialogActions } from '../../../../catalyst-components/dialog';
import { Field, Label } from '../../../../catalyst-components/fieldset';
import { Input } from '../../../../catalyst-components/input';
import { Id } from '../../../../convex/_generated/dataModel';
import { CompletedSessionsTable } from './completed-sessions-table';

export const ManageProgress: React.FC = () => {
    const navigate = useNavigate();
    const { convexUserId } = useContext(BaseContext);
    const resetProgress = useMutation(api.user_progress.resetUserProgress);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [confirmReset, setConfirmReset] = useState(false);

    // Acorn adjustment state
    const [showAcornDialog, setShowAcornDialog] = useState(false);
    const [newAcornBalance, setNewAcornBalance] = useState<string>('');
    const [acornError, setAcornError] = useState<string | null>(null);
    const [isSubmittingAcorn, setIsSubmittingAcorn] = useState(false);

    const userAcorns = useQuery(
        api.acorns.getUserAcorns,
        convexUserId ? { user_id: convexUserId as Id<"users"> } : 'skip'
    );
    const adminGrantAcorns = useMutation(api.acorns.adminGrantAcorns);

    const handleAcornAdjustment = async () => {
        if (!convexUserId) return;

        const targetBalance = parseInt(newAcornBalance, 10);
        if (isNaN(targetBalance) || targetBalance < 0) {
            setAcornError('Please enter a valid balance (0 or greater)');
            return;
        }

        const currentBalance = userAcorns?.balance ?? 0;
        const difference = targetBalance - currentBalance;

        if (difference === 0) {
            setShowAcornDialog(false);
            return;
        }

        setIsSubmittingAcorn(true);
        setAcornError(null);

        try {
            await adminGrantAcorns({
                user_id: convexUserId as Id<"users">,
                amount: difference,
                reason: 'Admin balance adjustment',
            });
            setShowAcornDialog(false);
            setNewAcornBalance('');
        } catch (err) {
            setAcornError('Failed to adjust acorns. Please try again.');
        } finally {
            setIsSubmittingAcorn(false);
        }
    };

    const openAcornDialog = () => {
        setNewAcornBalance(String(userAcorns?.balance ?? 0));
        setAcornError(null);
        setShowAcornDialog(true);
    };
    
    // Show loading state when user ID is not yet available
    if (!convexUserId) {
        return (
            <section className="min-h-screen bg-background pt-24 px-6 flex flex-col">
                <div className="text-center py-8">
                    <div className="text-lg text-gray-600">Loading user information...</div>
                </div>
            </section>
        );
    }

    const handleResetProgress = async () => {
        try {
            await resetProgress({
                user_id: convexUserId as Id<"users">
            });
            setAlertMessage('Progress reset successfully!');
            setShowAlert(true);
        } catch (error) {
            setAlertMessage('Error resetting progress. Please try again.');
            setShowAlert(true);
        }
        setConfirmReset(false);
    };

    return (
        <section className="min-h-screen bg-background pt-24 px-6 flex flex-col">
            <div className="flex flex-col justify-between mb-8">
                <h1 className="text-3xl font-bold">Manage Progress</h1>
                <div className="flex gap-2 mt-2 mb-2">
                    <Button
                        color="light"
                        className="w-32"
                        onClick={() => navigate('../')}
                    >
                        Go back
                    </Button>
                    <Button
                        color="light"
                        className="w-32"
                        onClick={() => navigate('/')}
                    >
                        Home
                    </Button>
                </div>
            </div>

            <div className="flex flex-col gap-4 max-w-md">
                <Button
                    color="red"
                    onClick={() => setConfirmReset(true)}
                >
                    Reset User Progress
                </Button>
            </div>

            {/* Acorn Adjustment Section */}
            <div className="mt-8 p-4 border border-amber-200 rounded-lg bg-amber-50 max-w-md">
                <h2 className="text-lg font-semibold mb-2">Acorn Balance</h2>
                <div className="flex items-center justify-between">
                    <div className="text-2xl font-bold text-amber-700">
                        {userAcorns?.balance ?? 0} acorns
                    </div>
                    <Button
                        color="amber"
                        onClick={openAcornDialog}
                    >
                        Set Balance
                    </Button>
                </div>
            </div>

            <CompletedSessionsTable userId={convexUserId as Id<"users">} />

            <ConfirmationDialog
                open={confirmReset}
                onClose={() => setConfirmReset(false)}
                onConfirm={handleResetProgress}
                title="Reset Progress"
                description="Are you sure you want to reset all progress? This will delete all sessions and progress data. This action cannot be undone."
                confirmText="Reset"
                cancelText="Cancel"
                isCritical={true}
            />

            <Alert open={showAlert} onClose={() => setShowAlert(false)} size="lg">
                <AlertTitle>Notification</AlertTitle>
                <AlertDescription>{alertMessage}</AlertDescription>
                <AlertActions>
                    <Button plain onClick={() => setShowAlert(false)}>
                        Ok
                    </Button>
                </AlertActions>
            </Alert>

            {/* Acorn Adjustment Dialog */}
            <Dialog open={showAcornDialog} onClose={() => setShowAcornDialog(false)} size="md">
                <DialogBody>
                    <DialogTitle>Set Acorn Balance</DialogTitle>

                    <div className="mt-6 space-y-4">
                        <Field>
                            <Label>New Balance</Label>
                            <Input
                                type="number"
                                min="0"
                                placeholder="Enter new balance"
                                value={newAcornBalance}
                                onChange={(e) => setNewAcornBalance(e.target.value)}
                            />
                        </Field>

                        {acornError && (
                            <p className="text-sm text-red-500">{acornError}</p>
                        )}
                    </div>

                    <DialogActions>
                        <Button color="light" onClick={() => setShowAcornDialog(false)} disabled={isSubmittingAcorn}>
                            Cancel
                        </Button>
                        <Button color="amber" onClick={handleAcornAdjustment} disabled={isSubmittingAcorn}>
                            {isSubmittingAcorn ? 'Saving...' : 'Save'}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </Dialog>
        </section>
    );
}; 